import { type FC, type ReactNode } from 'react';
import { format } from 'date-fns/fp';

import { FullPageContent } from '@/components/layout/full-page-content';
import { useMe } from '@/hooks/use-me';
import { plural, singular } from '@/utils/format/label';

interface BadgeProps {
  label: string;
}

const Badge: FC<BadgeProps> = ({ label }) => {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-gray-500 px-2 py-1 text-xs font-medium text-gray-600">
      {label}
    </span>
  );
};

export const ChangelogPage: FC = () => {
  const me = useMe();

  // Determine the specific wording based on me.config.type
  const fleetName =
    me.config.type === 'mobility'
      ? 'the entire fleet'
      : me.config.type === 'bess'
        ? 'all levels of the entire site'
        : 'the entire fleet';

  return (
    <FullPageContent
      className="relative space-y-12 px-[3.75rem] py-8"
      scrollable
    >
      <Change
        title="Version 3.5.0"
        date={new Date('2025-02-17')}
        content={
          <>
            <p>
              We've introduced significant improvements to user management,
              navigation, and analytics, making the platform more intuitive and
              more powerful.
            </p>
            <h4>New Features:</h4>
            <ul>
              <li>
                <strong>New Navigation:</strong>
                <ul>
                  <li>
                    Modernized UI: A refreshed, contemporary design for an
                    improved user experience.
                  </li>
                  <li>
                    Asset List & Geographical Map: The home page now includes an
                    asset list and an interactive geographical map for better
                    visibility and access.
                  </li>
                  <li>
                    Streamlined Workflow: Features have been reorganized, with
                    automatic pre-filtering when selecting an asset, allowing
                    quicker access to relevant data.
                  </li>
                </ul>
              </li>
              <li>
                <strong>User Management:</strong>
                <ul>
                  <li>
                    Expanded role-based access control, now supporting
                    asset/site-level permissions, third-party access, and
                    feature-specific controls.
                  </li>
                  <li>
                    Removal of Looker branding from the URL and landing page for
                    a seamless experience.
                  </li>
                </ul>
              </li>
              <li>
                <strong>PCS Analytics:</strong> We expand beyond batteries and
                the platform now includes a comprehensive PCS analytics tool.
                <ul>
                  <li>
                    PCS Alerts: This new feature includes two new types of
                    alerts that raise if there are issues regarding the PCS
                    efficiency or power availability.
                    <ul>
                      <li>
                        <Badge label="PCS Efficiency" /> – Notifies you when PCS
                        efficiency drops below defined levels, to warn of
                        operational issues or aging equipment.
                      </li>
                      <li>
                        <Badge label="Power Availability" /> – Alerts you about
                        the operational state and readiness of your PCSs.
                      </li>
                    </ul>
                  </li>
                  <li>
                    Dedicated PCS page to track and analyze PCS performance for
                    individual PCS or across entire sites. Compare your PCSs and
                    dive into historical trends.
                  </li>
                </ul>
              </li>
            </ul>
          </>
        }
      />
      <Change
        title="Version 3.4.0"
        date={new Date('2024-12-09')}
        content={
          <>
            <p>
              We're excited to announce the latest update to our platform,
              introducing powerful new features and enhancements to streamline
              alerting, improve visibility, and enhance usability. These updates
              are designed to provide more actionable insights and optimize your
              workflow.
            </p>
            <h4>New Features:</h4>
            <ul>
              <li>
                New Alerting: Units are now compared across {fleetName} to
                detect key performance issues such as safety anomalies, SOC
                imbalances or cycle deviations.
              </li>
              <li>
                Alert Preview and Sorting: Alerts are now categorized into alert
                types. Gain better context for alerts with the addition of
                supporting graphics in previews, helping you quickly assess the
                situation at a glance.
              </li>
              <li>
                Grouped Alerting: Instead of generating individual alerts for
                single systems, alerts are now grouped when adjacent systems
                experience similar issues. This reduces alert noise and
                simplifies corresponding tasks.
              </li>
              <li>
                Improved Recommendation Details: This page now focuses
                exclusively on recommendations, displaying only the affected
                systems to improve clarity and focus for users.
              </li>
            </ul>
          </>
        }
      />
      <Change
        title="Version 3.3.0"
        date={new Date('2024-11-21')}
        content={
          <>
            <p>
              There is a huge update to our platform with the introduction of
              the Reporting feature. This update streamlines insights and
              planning by providing scheduled reports directly in the Records
              app.
            </p>
            <h4>New Features:</h4>
            <ul>
              <li>
                Operational Report: Gain insights into daily operations to
                optimize performance.
              </li>
              <li>
                Insurance Report: Access detailed metrics for insurance
                requirements and compliance.
              </li>
              <li>
                Portfolio Report: Evaluate overall portfolio health with
                comprehensive metrics.
              </li>
            </ul>
          </>
        }
      />
      <Change
        title="Version 3.2.0"
        date={new Date('2024-08-26')}
        content={
          <>
            <p>
              The platform receives a new update with the introduction of the
              Records app. This update enhances asset management and
              transparency by providing a centralized location for all your
              asset information and related documentation.
            </p>
            <h4>New Features:</h4>
            <ul>
              <li>
                Comprehensive Asset Overview: Gain a clear, organized view of
                your assets, including metadata and performance data.
              </li>
              <li>
                Document Repository: Store, manage, and access all asset-related
                documents in one secure location.
              </li>
              <li>
                Metadata Insights: Review and update critical metadata for
                accurate and efficient asset tracking.
              </li>
              <li>
                Version History: Keep track of document versions for compliance
                and reference.
              </li>
              <li>
                Search and Filter: Quickly find specific assets or documents
                using powerful search and filter options.
              </li>
              <li>
                Integration Ready: Seamlessly connect with other ACCURE apps for
                a holistic management experience.
              </li>
            </ul>
          </>
        }
      />
      <Change
        title="Version 3.1.0"
        date={new Date('2024-07-22')}
        content={
          <>
            <p>
              Introduction of the Tasks app. This update enhances productivity
              and streamlines workflows with several new features designed to
              improve task and alert management.
            </p>
            <h4>New Features:</h4>
            <ul>
              <li>
                Kanban Board: Visual task tracking integrated with ACCURE
                alerts.
              </li>
              <li>
                Alert Management: Track and assign maintenance tasks, monitor
                progress.
              </li>
              <li>
                Collaborative Tasks: Work with colleagues on tasks and alerts.
              </li>
              <li>Due Dates: Set deadlines for tasks.</li>
              <li>File Uploads: Attach relevant documents to tasks.</li>
              <li>
                Direct Alert Interaction: Provide feedback, ask questions, reset
                scores.
              </li>
              <li>
                ACCURE Support Requests: Easily request support for issues.
              </li>
            </ul>
          </>
        }
      />
      <Change
        title="Version 3.0.0"
        date={new Date('2024-03-08')}
        content={
          <>
            <p>
              We're excited to announce a significant update to our Predictive
              Battery Analytics Platform. This update represents a major leap
              forward in providing the most holistic battery analysis tool
              available. With a focus on actionability, integration, user
              experience, and depth of analysis, we've made several enhancements
              and introduced new features that will help our users manage their
              battery assets more effectively and efficiently.
            </p>
            <p>
              Unified Platform: In our biggest change yet, we've consolidated
              our three core products — Safety Manager, Performance Manager, and
              Lifetime Manager — into a single, integrated platform. This
              streamlining means users can now access the full suite of ACCURE
              battery management tools in one place, simplifying the user
              experience and improving workflow efficiency.
            </p>

            <ul>
              <li>
                New Landing Page: We've introduced a landing page to provide
                asset-level information at a glance. Users can now see
                throughput, uptime, and active alerts for each asset, allowing
                for immediate assessment.
              </li>
              {me.config.type !== 'mobility' ? (
                <li>
                  Asset Navigator: The all-new Asset Navigator replaces and
                  consolidates all monitoring charts from the former individual
                  product dashboards. With the ability to visualize data across
                  multiple dimensions, selectable by user-defined filters, the
                  Asset Navigator offers unparalleled insight into your battery
                  assets.
                </li>
              ) : (
                <li>
                  Fleet Navigator: The all-new Fleet Navigator replaces and
                  consolidates all monitoring charts from the former individual
                  product dashboards. With the ability to visualize data across
                  multiple dimensions, selectable by user-defined filters, the
                  Fleet Navigator offers unparalleled insight into your battery
                  assets.
                </li>
              )}
              <li>
                Alert Viewer: The new Alert Viewer now features a sub-navigation
                structure that allows users to quickly survey all currently
                active alerts and delve into detailed analyses with ease.
                Additionally, by merging the alerting logic from both the
                Performance Manager and the Safety Manager, all alerts are now
                visible within the same page.
              </li>
              <li>
                Recommendation Details: The Safety Manager's Recommendation
                Details page has been significantly expanded. It now
                incorporates vital information from the details pages of the
                Performance and Lifetime Managers, offering a more holistic view
                of all classes of recommendations.
              </li>
              <li>
                Data Explorer: We've introduced the Data Explorer, a powerful
                new tool designed for advanced analysis of (un)filtered
                timeseries data. This feature allows users to explore the data
                underpinning our analyses, facilitating a deeper understanding
                and investigation of battery performance over time.
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 2.5.2"
        date={new Date('2024-02-05')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>
                Moved deactivated{' '}
                {plural(me.config.top.labels.name).toLowerCase()} to Data
                Quality page
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 2.5.1"
        date={new Date('2024-02-01')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>Improved Safety Score</li>
              {me.config.id === '7' ? (
                <>
                  <li>
                    Improved Dominant Cell Fade indicator (alert type =
                    Capacity)
                  </li>
                  <ul>
                    <li>improved alert descriptions</li>
                  </ul>
                </>
              ) : null}
            </ul>
          </>
        }
      />

      <Change
        title="Version 2.5.0"
        date={new Date('2024-01-19')}
        content={
          <>
            <p>Added:</p>

            <ul>
              <li>
                Added new indicator to Data Monitor to check for units with very
                few plausible data points
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 2.4.0"
        date={new Date('2024-01-15')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>Improved Suggested Actions</li>
              <ul>
                <li>
                  added more specific recommendations based on Safety Score and
                  Indicator
                </li>
                <li>
                  added suggestions for each{' '}
                  {singular(me.config.unit.labels.name).toLowerCase()}, found by
                  filtering for{' '}
                  {singular(me.config.unit.labels.name).toLowerCase()} in Safety{' '}
                  {singular(me.config.top.labels.name)} Status dashboard
                </li>
              </ul>
            </ul>
          </>
        }
      />

      <Change
        title="Version 2.3.0"
        date={new Date('2023-11-17')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>
                Improved Faulty State Estimation indicator (alert type = BMS)
              </li>
              <ul>
                <li>
                  added `unresponsive_voltage` variant to detect high currents
                  without appropriate voltage changes
                </li>
              </ul>
              <li>Improved Safety Score</li>
              <ul>
                <li>
                  fixed some units not becoming -1 instead of 0 when appropriate
                </li>
              </ul>
            </ul>
          </>
        }
      />

      <Change
        title="Version 2.2.2"
        date={new Date('2023-11-10')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>Improved Safety Score</li>
            </ul>
          </>
        }
      />

      {me.config.id === '7' ? (
        <Change
          title="Version 2.2.1"
          date={new Date('2023-11-03')}
          content={
            <>
              <p>Improvements:</p>

              <ul>
                <li>
                  Improved Dominant Cell Fade indicator (alert type = Capacity)
                </li>
                <ul>
                  <li>adjusted for seasonal behavior</li>
                </ul>
              </ul>
            </>
          }
        />
      ) : null}

      <Change
        title="Version 2.2.0"
        date={new Date('2023-10-23')}
        content={
          <>
            <p>Added:</p>

            <ul>
              <li>
                Released new dashboard "Safety{' '}
                {singular(me.config.unit.labels.name)} Alerts Overview"
              </li>
            </ul>

            {me.config.id === '7' ? (
              <>
                <p>Improvements:</p>

                <ul>
                  <li>
                    Improved Dominant Cell Fade indicator (alert type =
                    Capacity) by adding monitoring of cell 8
                  </li>
                </ul>
              </>
            ) : me.config.id === '24' ? (
              <>
                <p>Improvements:</p>

                <ul>
                  <li>
                    Improved Out-of-Spec Operation indicator (alert type = BMS)
                  </li>
                </ul>
              </>
            ) : null}
          </>
        }
      />

      <Change
        title="Version 2.1.1"
        date={new Date('2023-10-05')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>
                Improved Out-of-Spec Operation indicator (alert type = BMS)
              </li>
              {me.config.id === '7' ? (
                <ul>
                  <li>
                    added dedicated checks to monitor operational states that
                    are not safety critical
                  </li>
                </ul>
              ) : null}
            </ul>
          </>
        }
      />

      <Change
        title="Version 2.1.0"
        date={new Date('2023-09-07')}
        content={
          <>
            <p>Improvements:</p>
            <ul>
              {me.config.id === '7' ? (
                <li>
                  Improved Dominant Cell Fade indicator (alert type = Capacity)
                </li>
              ) : null}
              <li>
                Improved Capacity Anomalies indicator (alert type = Capacity)
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 2.0.0"
        date={new Date('2023-08-30')}
        content={
          <>
            <p>Added:</p>

            <ul>
              <li>
                Show Safety Score visualization (Score line chart and alert
                detection bar chart)
              </li>
              <li>
                Add extra columns to "Number of critical{' '}
                {plural(me.config.top.labels.name)}"-tiles drill field
              </li>
              {singular(me.config.top.labels.name) !==
              singular(me.config.unit.labels.name) ? (
                <li>
                  Add filter for {me.config.unit.labels.id} on "Safety{' '}
                  {singular(me.config.top.labels.name)} Status" dashboard
                </li>
              ) : null}
            </ul>

            <p>Improvements:</p>

            <ul>
              <li>
                Consolidate "Status" and "History" dashboards into "Safety{' '}
                {singular(me.config.top.labels.name)} Status" dashboard
              </li>
              {me.config.id === '19' ? (
                <>
                  <li>
                    Improved Capacity Anomalies indicator (alert type =
                    Capacity)
                  </li>
                  <li>
                    Improved Impedence Anomalies indicator (alert type =
                    Impedence)
                  </li>
                </>
              ) : null}
            </ul>
          </>
        }
      />

      {me.config.id === '7' ? (
        <Change
          title="Version 1.21.1"
          date={new Date('2023-08-21')}
          content={
            <>
              <p>Improvements:</p>

              <ul>
                <li>Improved Dominant Cell Fade indicator</li>
              </ul>
            </>
          }
        />
      ) : null}

      <Change
        title="Version 1.21.0"
        date={new Date('2023-08-18')}
        content={
          <>
            {me.config.id === '24' ? (
              <>
                <p>Added:</p>

                <ul>
                  <li>Onboarded Crosset sites A and B</li>
                </ul>
              </>
            ) : null}

            <p>Improvements:</p>

            <ul>
              <li>
                Improved Faulty State Estimation indicator (alert type = BMS)
                for LFP {plural(me.config.top.labels.name)}
              </li>
              {me.config.id === '19' ? (
                <ul>
                  <li>increased voltage thresholds to match BMS limits</li>
                </ul>
              ) : null}
              {me.config.id === '24' ? (
                <li>
                  Fixed {singular(me.config.unit.labels.name)} IDs to begin at
                  string_01 and sort {singular(me.config.unit.labels.name)}{' '}
                  ordering
                </li>
              ) : null}
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.20.0"
        date={new Date('2023-08-11')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>
                Improved Faulty State Estimation indicator (alert type = BMS)
              </li>
              <ul>
                <li>
                  added detection of lost modules for LFP{' '}
                  {plural(me.config.top.labels.name)}
                </li>
              </ul>
              {me.config.id === '18' ? (
                <li>
                  Improved Out-of-Spec Operation indicator (alert type = BMS)
                </li>
              ) : null}
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.19.0"
        date={new Date('2023-07-31')}
        content={
          <>
            <p>Added:</p>

            <ul>
              <li>
                Added "Description" to {singular(me.config.unit.labels.name)}{' '}
                alert tables in "Safety {singular(me.config.top.labels.name)}{' '}
                Status" and "Safety {singular(me.config.top.labels.name)}{' '}
                History" dashboards
              </li>
            </ul>

            <p>Improvements:</p>

            <ul>
              <li>
                Moved `Last Analysis` to "Current{' '}
                {singular(me.config.unit.labels.name)} Scoring" table on "Safety{' '}
                {singular(me.config.top.labels.name)} Status" dashboard
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.18.0"
        date={new Date('2023-07-06')}
        content={
          <>
            <p>Added:</p>

            <ul>
              <li>
                Added link to Data Monitor page on "Safety Alerts" and "Safety{' '}
                {singular(me.config.top.labels.name)} Status" dashboard
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.17.0"
        date={new Date('2023-06-23')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>
                Improve "Safety Alerts" table on Safety{' '}
                {singular(me.config.top.labels.name)} Status dashboard
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.16.0"
        date={new Date('2023-06-16')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>
                Improved Out-of-Spec Operation indicator (alert type = BMS)
              </li>
              <ul>
                <li>
                  added sensor ids, where available, to Out-of-Spec Operation
                  alerts
                </li>
              </ul>
              {me.config.id === '18' ? (
                <li>
                  Improved Capacity Anomalies indicator (alert type = Capacity)
                </li>
              ) : null}
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.15.0"
        date={new Date('2023-06-06')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>
                Improved Capacity Anomalies indicator (alert type = Capacity)
              </li>
              {me.config.id === '19' ? (
                <li>
                  Improved Out-of-Spec Operation indicator (alert type = BMS)
                </li>
              ) : null}
            </ul>

            <p>Added:</p>

            <ul>
              <li>
                Added filter for operational status on the Safety Alerts page
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.14.0"
        date={new Date('2023-05-30')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>
                Improved Faulty State Estimation indicator (alert type = BMS)
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.13.0"
        date={new Date('2023-05-05')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>
                Implemented display of same-day critical alerts and warnings
              </li>
              <li>Retired display of inactive units within active systems</li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.12.0"
        date={new Date('2023-04-27')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>
                Improved analyzed and not analyzed tiles on Alert Dashboard
              </li>
              <li>Fixed bug in filter of not analyzed systems</li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.11.0"
        date={new Date('2023-04-12')}
        content={
          <>
            <p>Added:</p>

            <ul>
              <li>
                Introduction of the field “Operational Status”, which shows the
                current operational status of a battery
              </li>
            </ul>

            <p>Improvements:</p>

            <ul>
              <li>
                Improved Capacity Anomalies indicator (alert type = Capacity)
              </li>
              <li>
                Improved Impedence Anomalies indicator (alert type = Impedence)
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.10.0"
        date={new Date('2023-03-16')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>Released redesigned dashboards</li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.10.0"
        date={new Date('2023-03-16')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>Released redesigned dashboards</li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.9.0"
        date={new Date('2023-03-07')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>
                Improved Capacity Anomalies indicator (alert type = Capacity)
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.8.0"
        date={new Date('2023-03-02')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>
                Improved Capacity Anomalies indicator (alert type = Capacity)
              </li>
              <li>
                Improved System Inactivity indicator (alert type = Data Quality)
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.7.1"
        date={new Date('2023-02-15')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>
                "number of systems with warning"-tile counts only safety
                warnings (no safety informations, no data issues)
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.7.0"
        date={new Date('2023-02-03')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>Improved analysis reduces System Inactivity alerts</li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.6.0"
        date={new Date('2023-01-25')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>
                Adjustments to improve the dashboard view (e.g. column names)
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.5.0"
        date={new Date('2023-01-23')}
        content={
          <>
            <p>Added:</p>

            <ul>
              <li>
                New filters for the "Analyze Window" and "Severity" are
                available in the Alert Status dashboard.
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.4.0"
        date={new Date('2023-01-18')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>
                Reduce non-critical alert severities beyond a certain time
                window
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.3.0"
        date={new Date('2023-01-04')}
        content={
          <>
            <p>Added:</p>

            <ul>
              <li>
                Sensor IDs are displayed in System Status and History Dashboard
                where applicable
              </li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.2.0"
        date={new Date('2022-12-20')}
        content={
          <>
            <p>Improvements:</p>

            <ul>
              <li>System Inactivity indicator</li>
              <li>Out-of-Spec Operation indicator</li>
              <li>Capacity Anomalies indicator</li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.1.0"
        date={new Date('2022-12-14')}
        content={
          <>
            <p>Added:</p>

            <ul>
              <li>Hot Spot Occurrence indicator</li>
              <li>Cooling Performance Anomalies indicator</li>
              <li>First and Last Detection Dates</li>
            </ul>

            <p>Improvements:</p>

            <ul>
              <li>Capacity Anomalies indicator</li>
              <li>Temperature Anomalies indicator</li>
              <li>Data Onboarding / Transform</li>
            </ul>
          </>
        }
      />

      <Change
        title="Version 1.0.0"
        content={
          <>
            <p>Added:</p>

            <ul>
              <li>Alert Page</li>
              <li>System Status Page</li>
              <li>System Alert History Page</li>
              <li>Capacity Anomalies indicator</li>
              <li>Impedence Anomalies indicator</li>
              <li>Temperature Anomalies indicator</li>
              <li>Out-of-Spec Operation indicator</li>
              <li>Data Unavailability indicator</li>
              <li>System Inactivity indicator</li>
              <li>Data Implausibility indicator</li>
            </ul>
          </>
        }
      />
    </FullPageContent>
  );
};

const formatDate = format('yyyy-MM-dd');

export type ChangeProps = {
  title: string;
  date?: Date;
  content: ReactNode;
};

export function Change({ title, date, content }: ChangeProps) {
  return (
    <div className="space-y-4">
      <div>
        {date ? (
          <p className="text-sm font-semibold leading-7 text-teal-700">
            {formatDate(date)}
          </p>
        ) : null}
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">
          {title}
        </h2>
      </div>
      <div className="prose">{content}</div>
    </div>
  );
}
